import { FC, useCallback, useEffect, useState } from 'react';

import { Select } from 'antd';
import { Button as AntBtn } from 'antd';
import { Button, Input, Label, Table } from 'ui';

import { defaultFieldValue } from '../save-contact/saveContact';
import s from './FieldToSave.module.scss';

interface FieldToSaveProps {
  fields?: {
    caption: string;
    value: string;
  }[];
  setFields: (
    fields: {
      caption: string;
      value: string;
    }[],
  ) => void;
}

const FieldToSave: FC<FieldToSaveProps> = ({ fields, setFields }) => {
  const [isEditing, setIsEditing] = useState<boolean>(false);

  const [selectedFieldValue, setSelectedFieldValue] = useState<string | undefined>();
  const [selectedFieldCaption, setSelectedFieldCaption] = useState<string | undefined>();
  const [selectedFields, setSelectedFields] = useState<
    {
      caption: string;
      value: string;
    }[]
  >([]);

  const [errors, setErrors] = useState({
    selectedFieldValue: false,
    selectedFieldCaption: false,
  });

  const handleAddOrUpdate = useCallback(() => {
    const newErrors = {
      selectedFieldValue: !selectedFieldValue,
      selectedFieldCaption: !selectedFieldCaption,
    };

    setErrors(newErrors);
    if (!selectedFieldValue || !selectedFieldCaption) return;
    const updatedFields = (() => {
      const exists = selectedFields?.findIndex((item) => item.value === selectedFieldValue);

      const newEntry = {
        caption: selectedFieldCaption,
        value: selectedFieldValue,
      };

      return exists !== -1
        ? selectedFields?.map((item, idx) => (idx === exists ? newEntry : item))
        : [...selectedFields!, newEntry];
    })();

    if (!updatedFields) {
      return;
    }
    setIsEditing(false);

    setFields(updatedFields);
    setSelectedFields(updatedFields); // ✅ Update child display state
    setErrors({ selectedFieldValue: false, selectedFieldCaption: false });
  }, [selectedFields, setIsEditing, selectedFieldCaption, selectedFieldValue, setFields]);

  useEffect(() => {
    if (fields && fields.length > 0) {
      setSelectedFields(fields);
    }
  }, [fields]);

  useEffect(() => {
    if (!isEditing && selectedFieldValue && defaultFieldValue[selectedFieldValue]) {
      setSelectedFieldCaption(defaultFieldValue[selectedFieldValue]);
    }
  }, [selectedFieldValue, isEditing]);

  const handleEdit = (row: any) => {
    setIsEditing(true);
    setSelectedFieldValue(row.value);
    setSelectedFieldCaption(row.caption);
    setErrors({ selectedFieldValue: false, selectedFieldCaption: false });
  };

  const handleDelete = async (row: any) => {
    const filtered = selectedFields?.filter((item) => item.value !== row.value);
    setSelectedFields(filtered);
    setFields(filtered);
  };

  const columns = [
    {
      title: 'Field Value',
      dataIndex: 'value',
      key: 'value',
    },
    {
      title: 'Field Caption',
      dataIndex: 'caption',
      key: 'caption',
    },
    {
      title: 'Action',
      key: 'action',
      render: (row) => (
        <div className={s.actionButtons}>
          <AntBtn htmlType="button" type="link" onClick={() => handleEdit(row)}>
            Edit
          </AntBtn>
          <AntBtn htmlType="button" type="link" onClick={() => handleDelete(row)}>
            Delete
          </AntBtn>
        </div>
      ),
    },
  ] as any;

  return (
    <div className={s.wrapper}>
      <div className={s.labelWrapper}>
        <Label>
          The key should be the caption, whilst the value should be reserved words to indicate from
          where fields should be taken.
        </Label>
      </div>

      <div className={s.content}>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            flexDirection: 'column',
            justifyContent: 'center',
          }}>
          <div className={s.inputWrapper}>
            <div className={`${s.inputBlock} ${errors.selectedFieldValue ? s.error : ''}`}>
              <Label required>Field Value</Label>
              <Select
                className={s.fieldSelect}
                placeholder="Select field value"
                value={selectedFieldValue}
                onChange={(value) => {
                  setSelectedFieldValue(value);
                  setErrors(() => ({
                    selectedFieldCaption: false,
                    selectedFieldValue: false,
                  }));
                  setIsEditing(false);
                  setSelectedFieldCaption(defaultFieldValue[value]); // Optional auto-fill
                }}
                options={Object.entries(defaultFieldValue).map(([value, label]) => ({
                  value,
                  label,
                }))}
              />
            </div>
          </div>

          <div className={s.inputWrapper}>
            <div className={`${s.inputBlock} ${errors.selectedFieldCaption ? s.error : ''}`}>
              <Label required>Field caption(Key)</Label>
              <Input
                name="fieldCaption"
                style={{ width: '100%' }}
                value={selectedFieldCaption}
                onChange={(e) => {
                  setErrors((prev) => ({
                    ...prev,
                    selectedFieldCaption: false,
                  }));
                  setSelectedFieldCaption(e.target.value);
                }}
              />
            </div>
          </div>

          <Button htmlType="button" fullWidth onClick={handleAddOrUpdate}>
            {isEditing ? 'Edit' : 'Add'}
          </Button>
        </div>

        <div>
          {!!selectedFields?.length && (
            <Table dataSource={selectedFields} rowKey={(row) => row.value} columns={columns} />
          )}
        </div>
      </div>
    </div>
  );
};

export default FieldToSave;
